import { useParams, useNavigate } from 'react-router';
import { Title, Button, Flex, Loader, Alert, Box, Center, Stack, Text } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../lib/trpc';
import EditForm from '../components/EditForm';
import ChildCollection from '../components/ChildCollection/ChildCollection';
import type { SectionConfig } from '../lib/types';

interface SectionEditProps {
  config: SectionConfig;
}

export default function SectionEdit({ config }: SectionEditProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const numericId = isNew ? null : Number(id);

  const utils = trpc.useUtils();

  const getQuery = trpc.admin.crud.get.useQuery(
    { entity: config.entity, id: numericId ?? 0 },
    { enabled: !isNew && numericId !== null && !Number.isNaN(numericId) },
  );

  const createMutation = trpc.admin.crud.create.useMutation({
    onSuccess: async () => {
      await utils.admin.crud.list.invalidate({ entity: config.entity });
      navigate('..');
    },
    onError: (err) => notifications.show({ color: 'red', message: err.message }),
  });

  const updateMutation = trpc.admin.crud.update.useMutation({
    onSuccess: async () => {
      await utils.admin.crud.list.invalidate({ entity: config.entity });
      navigate('..');
    },
    onError: (err) => notifications.show({ color: 'red', message: err.message }),
  });

  function handleSubmit(data: Record<string, unknown>) {
    if (isNew) {
      createMutation.mutate({ entity: config.entity, data });
    } else if (numericId !== null) {
      updateMutation.mutate({ entity: config.entity, id: numericId, data });
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isLoading = !isNew && getQuery.isLoading;

  const title = isNew ? `New ${config.title}` : `Edit ${config.title}`;

  return (
    <Box>
      <Flex align="center" gap="md" mb="lg">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('..')}>
          Back
        </Button>
        <Title order={2}>{title}</Title>
      </Flex>

      {isLoading && <Center h="60vh"><Loader /></Center>}
      {!isNew && getQuery.isError && <Alert color="red">Record not found.</Alert>}

      {(isNew || (!isLoading && getQuery.data !== undefined)) && (
        <EditForm
          fields={config.formFields}
          initialData={isNew ? null : (getQuery.data as Record<string, unknown> ?? null)}
          onSubmit={handleSubmit}
          onCancel={() => navigate('..')}
          isSubmitting={isSubmitting}
        />
      )}

      {config.childCollections && (
        <Stack mt="xl">
          {numericId === null
            ? <Text c="dimmed">Save the record first to add {config.childCollections.map((c) => c.title.toLowerCase()).join(', ')}.</Text>
            : config.childCollections.map((child) => (
              <ChildCollection key={child.entity} config={child} parentId={numericId} />
            ))}
        </Stack>
      )}
    </Box>
  );
}
